import React, {Fragment, ReactNode} from "react";
import {Popover, Transition} from "@headlessui/react";
import cx from "classnames";
import {HiChevronDown, HiOutlineAdjustments} from "react-icons/hi";

export type FilterChipConfig = {
    key: string;
    label: string;
    summary?: string;
    active?: boolean;
    align?: "left" | "right";
    className?: string;
    panelClassName?: string;
    onClear?: () => void;
    children: ReactNode;
};

type FilterChipPopoverProps = Omit<FilterChipConfig, "key">;

export function FilterChipPopover({
    label,
    summary,
    active,
    align = "left",
    className,
    panelClassName,
    onClear,
    children
}: FilterChipPopoverProps) {
    return (
        <Popover className={cx("relative inline-flex w-full sm:w-auto", className)}>
            {({open, close}) => (
                <>
                    <Popover.Button
                        className={cx(
                            "group inline-flex w-full min-w-0 items-center gap-3 rounded-full border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400 sm:w-auto",
                            active || open
                                ? "border-indigo-500 bg-indigo-50 text-gray-900 ring-1 ring-indigo-100 dark:border-indigo-400 dark:bg-indigo-900/50 dark:text-gray-50 dark:ring-indigo-800/50"
                                : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                        )}
                    >
                        <span
                            className={cx(
                                "flex h-2 w-2 rounded-full",
                                active || open ? "bg-indigo-500 shadow-sm" : "bg-gray-300 dark:bg-gray-600"
                            )}
                            aria-hidden
                        />
                        <div className="flex min-w-0 flex-1 flex-col items-start leading-tight">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                                {label}
                            </span>
                            <span className="text-xs text-gray-700 dark:text-gray-200 truncate">
                                {summary || "Any"}
                            </span>
                        </div>
                        <HiChevronDown
                            className={cx(
                                "h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:text-gray-600 dark:group-hover:text-gray-200",
                                open && "rotate-180"
                            )}
                        />
                    </Popover.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-150"
                        enterFrom="opacity-0 translate-y-1 scale-95"
                        enterTo="opacity-100 translate-y-0 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 translate-y-0 scale-100"
                        leaveTo="opacity-0 translate-y-1 scale-95"
                    >
                        <Popover.Panel
                            className={cx(
                                "absolute z-30 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-700 dark:bg-gray-900",
                                align === "right" ? "right-0" : "left-0",
                                panelClassName
                            )}
                        >
                            <div className="flex items-start justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                    <span className="rounded-md bg-indigo-50 p-1 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                                        <HiOutlineAdjustments className="h-4 w-4"/>
                                    </span>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</span>
                                        {summary && <span className="text-xs text-gray-500 dark:text-gray-400">{summary}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {onClear && (
                                        <button
                                            type="button"
                                            className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                                            onClick={() => {
                                                onClear();
                                            }}
                                        >
                                            Reset
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                                        onClick={() => close()}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 space-y-3 text-gray-900 dark:text-gray-100">
                                {children}
                            </div>
                        </Popover.Panel>
                    </Transition>
                </>
            )}
        </Popover>
    );
}

export function FilterChipGroup({filters, className}: { filters: FilterChipConfig[]; className?: string }) {
    return (
        <div className={cx("flex flex-wrap items-center gap-2", className)}>
            {filters.map((filter) => (
                <FilterChipPopover
                    key={filter.key}
                    label={filter.label}
                    summary={filter.summary}
                    active={filter.active}
                    align={filter.align}
                    className={filter.className}
                    panelClassName={filter.panelClassName}
                    onClear={filter.onClear}
                >
                    {filter.children}
                </FilterChipPopover>
            ))}
        </div>
    );
}
